// src/pages/Profile.tsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import { useLookups } from '../context/LookupContext';
import { LookupValue } from '../services/lookupService';
import {
  fetchProfileData,
  saveOrUpdateProfileRecord,
  fetchDependentsData,
  saveOrUpdateDependentRecord,
  deleteDependentRecord,
} from '../services/profileService';
import { Edit, Plus, Save, X, RefreshCw } from 'lucide-react';

// Import Child Components
import Notification from '../components/shared/Notification';
import ProfileCard from '../components/profile/ProfileCard';
import DependentsSection from '../components/profile/DependentsSection';
import ProfileEditModal from '../components/profile/ProfileEditModal';
import DependentFormModal from '../components/profile/DependentFormModal';

export interface ProfileRecord {
  id: string; name: string; relation: string; profileType: string; dob: string; gender: string; maritalStatus: string; country: string; state: string; city: string; currency: string; employmentStatus: string; retirementAge: number; profilePicture: string;
}
export interface DependentRecord {
  id: string; name: string; relation: string; profileType: string; dob: string; gender: string; maritalStatus: string; country: string; state: string; city: string; currency: string; employmentStatus: string; retirementAge: number;
}

const Profile: React.FC = () => {
    // All original state, refs, and hooks are preserved
    const { state, dispatch } = useAppContext();
    const { getLabel, getOptions, getStates, getCities, countries, currencies, isLoading: isLookupsLoading } = useLookups();

    const [isEditing, setIsEditing] = useState(false);
    const [showAddDependent, setShowAddDependent] = useState(false);
    const [editingDependentId, setEditingDependentId] = useState<string | null>(null);
    const [apiLoading, setApiLoading] = useState(false);
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const [isSubmittingDependent, setIsSubmittingDependent] = useState(false);
    const [operationStatus, setOperationStatus] = useState({ show: false, message: '', type: 'success' as 'success' | 'error' });
    
    const [profileData, setProfileData] = useState<ProfileRecord | null>(null);
    const [dependentsData, setDependentsData] = useState<DependentRecord[]>([]);

    const [editedProfile, setEditedProfile] = useState<ProfileRecord>({ id: '', name: '', relation: '', profileType: '1', dob: '', gender: '', maritalStatus: '', country: '', state: '', city: '', currency: '', employmentStatus: '', retirementAge: 0, profilePicture: '' });
    const [dependentForm, setDependentForm] = useState<DependentRecord>({ id: '', name: '', relation: '', profileType: '2', dob: '', gender: '', maritalStatus: '', country: '', state: '', city: '', currency: '', employmentStatus: '', retirementAge: 0 });

    const [statesForProfile, setStatesForProfile] = useState<LookupValue[]>([]);
    const [citiesForProfile, setCitiesForProfile] = useState<LookupValue[]>([]);
    const [statesForDependent, setStatesForDependent] = useState<LookupValue[]>([]);
    const [citiesForDependent, setCitiesForDependent] = useState<LookupValue[]>([]);

    const getCurrencySymbol = (currencyCode: string): string => {
        if (!currencyCode) return '₹';
        try {
            const parts = new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode, currencyDisplay: 'symbol' }).formatToParts(1);
            const symbolPart = parts.find(part => part.type === 'currency');
            return symbolPart ? symbolPart.value : currencyCode;
        } catch (e) { return '₹'; }
    };

    const updateCurrency = useCallback((currencyCode: string) => {
        if (currencyCode) {
            const symbol = getCurrencySymbol(currencyCode);
            localStorage.setItem('currencySymbol', symbol);
            dispatch({ type: 'SET_CURRENCY_SYMBOL', payload: symbol });
        }
    }, [dispatch]);
    
    const transformApiProfileToRecord = (apiData: any): ProfileRecord => ({
        id: apiData?.id || '', name: apiData?.name || '', relation: apiData?.relation || '', profileType: apiData?.profile_type || '', dob: apiData?.date_of_birth || '', gender: apiData?.gender || '', maritalStatus: apiData?.marital_status || '', country: apiData?.country || '', state: apiData?.state || '', city: apiData?.city || '', currency: apiData?.currency || '', employmentStatus: apiData?.employment_status || '', retirementAge: apiData?.planned_retirement_age || 0, profilePicture: apiData?.profile_picture || ''
    });

    const transformApiDependentToRecord = (apiData: any): DependentRecord => ({
        id: apiData?.id || '', name: apiData?.name || '', relation: apiData?.relation || '', profileType: apiData?.profile_type || '', dob: apiData?.date_of_birth || '', gender: apiData?.gender || '', maritalStatus: apiData?.marital_status || '', country: apiData?.country || '', state: apiData?.state || '', city: apiData?.city || '', currency: apiData?.currency || '', employmentStatus: apiData?.employment_status || '', retirementAge: apiData?.planned_retirement_age || 0
    });

    const loadAndCategorizeProfileData = useCallback(async () => {
        setApiLoading(true);
        try {
            const profileResponse = await fetchProfileData();
            if (profileResponse && profileResponse.data) {
                const selfProfileRecord = transformApiProfileToRecord(profileResponse.data);
                setProfileData(selfProfileRecord);
                setEditedProfile(selfProfileRecord);
                dispatch({ type: 'UPDATE_PROFILE', payload: selfProfileRecord });
                updateCurrency(selfProfileRecord.currency);
            }
        } catch (error) {
            console.error('Failed to load profile data:', error);
            setOperationStatus({ show: true, message: 'Failed to load profile.', type: 'error' });
        } finally {
            setApiLoading(false);
        }
    }, [dispatch, updateCurrency]);

    const loadAndCategorizeDependData = useCallback(async () => {
        setApiLoading(true);
        try {
            const response = await fetchDependentsData();
            if (response && Array.isArray(response.data)) {
                const records = response.data.map(transformApiDependentToRecord).filter(dep => dep.profileType !== '1');
                setDependentsData(records);
                dispatch({ type: 'SET_DEPENDENTS', payload: records });
            } else {
                setDependentsData([]);
                dispatch({ type: 'SET_DEPENDENTS', payload: [] });
            }
        } catch (error) {
            console.error('Failed to load dependent data:', error);
            setOperationStatus({ show: true, message: 'Failed to load dependents.', type: 'error' });
        } finally {
            setApiLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        loadAndCategorizeProfileData();
        loadAndCategorizeDependData();
    }, [loadAndCategorizeProfileData, loadAndCategorizeDependData]);

    useEffect(() => {
        if (editedProfile.country) getStates(editedProfile.country).then(states => {
            setStatesForProfile(states);
            if (!states.some(s => s.value === editedProfile.state)) setEditedProfile(p => ({ ...p, state: '', city: '' }));
        }); else { setStatesForProfile([]); setCitiesForProfile([]); }
    }, [editedProfile.country, getStates]);

    useEffect(() => {
        if (editedProfile.country && editedProfile.state) getCities(editedProfile.country, editedProfile.state).then(cities => {
            setCitiesForProfile(cities);
            if (!cities.some(c => c.value === editedProfile.city)) setEditedProfile(p => ({ ...p, city: '' }));
        }); else { setCitiesForProfile([]); }
    }, [editedProfile.state, editedProfile.country, getCities]);

    useEffect(() => {
        if (dependentForm.country) getStates(dependentForm.country).then(states => {
            setStatesForDependent(states);
            if (!states.some(s => s.value === dependentForm.state)) setDependentForm(p => ({ ...p, state: '', city: '' }));
        }); else { setStatesForDependent([]); setCitiesForDependent([]); }
    }, [dependentForm.country, getStates]);

    useEffect(() => {
        if (dependentForm.country && dependentForm.state) getCities(dependentForm.country, dependentForm.state).then(cities => {
            setCitiesForDependent(cities);
            if (!cities.some(c => c.value === dependentForm.city)) setDependentForm(p => ({ ...p, city: '' }));
        }); else { setCitiesForDependent([]); }
    }, [dependentForm.state, dependentForm.country, getCities]);
    
    const handleProfileSave = async () => {
      console.log("handleProfileSave called");
        setIsSavingProfile(true);
        try {
            await saveOrUpdateProfileRecord({ ...editedProfile, profileType: '1' });
            await loadAndCategorizeProfileData();
            setIsEditing(false);
            setOperationStatus({ show: true, message: 'Profile updated successfully!', type: 'success' });
        } catch (error: any) {
            setOperationStatus({ show: true, message: error.message || 'Failed to save profile.', type: 'error' });
        } finally {
            setIsSavingProfile(false);
        }
    };

    const handleDependentSubmit = async () => {
        if (!dependentForm.name || !dependentForm.profileType) {
            return setOperationStatus({ show: true, message: "Name and Profile Type are required.", type: 'error' });
        }
        if (dependentForm.profileType === '1') {
            setOperationStatus({ show: true, message: "A dependent cannot have the profileType 'Self'.", type: 'error' });
            return;
        }
        setIsSubmittingDependent(true);
        try {
            await saveOrUpdateDependentRecord({ ...dependentForm, id: editingDependentId || '', retirementAge: dependentForm.retirementAge || 0 });
            await loadAndCategorizeDependData();
            setDependentForm({ id: '', name: '', relation: '', profileType: '2', dob: '', gender: '', maritalStatus: '', country: '', state: '', city: '', currency: '', employmentStatus: '', retirementAge: 0 });
            setEditingDependentId(null);
            setShowAddDependent(false);
            setOperationStatus({ show: true, message: `Dependent ${editingDependentId ? 'updated' : 'added'}!`, type: 'success' });
        } catch (error: any) {
            setOperationStatus({ show: true, message: error.message || 'Failed to save dependent.', type: 'error' });
        } finally {
            setIsSubmittingDependent(false);
        }
    };

    const handleDeleteDependent = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this dependent?')) {
            try {
                await deleteDependentRecord(id);
                setDependentsData(prev => prev.filter(dep => dep.id !== id));
                setOperationStatus({ show: true, message: 'Dependent deleted successfully!', type: 'success' });
            } catch (error: any) {
                setOperationStatus({ show: true, message: error.message || 'Failed to delete dependent.', type: 'error' });
            }
        }
    };
    
    const handleEditDependent = (dependent: DependentRecord) => {
        setDependentForm(dependent);
        setEditingDependentId(dependent.id);
        setShowAddDependent(true);
    };

    const handleAddDependent = () => {
        setEditingDependentId(null);
        setDependentForm({ id: '', name: '', relation: '', profileType: '2', dob: '', gender: '', maritalStatus: '', country: '', state: '', city: '', currency: '', employmentStatus: '', retirementAge: 0 });
        setShowAddDependent(true);
    };

    const handleCancelDependent = () => {
        setDependentForm({ id: '', name: '', relation: '',  profileType: '2', dob: '', gender: '', maritalStatus: '', country: '', state: '', city: '', currency: '', employmentStatus: '', retirementAge: 0 });
        setEditingDependentId(null);
        setShowAddDependent(false);
    };

    const handleProfilePictureChange = (event: React.ChangeEvent<HTMLInputElement>) => { /* Your logic here */ };

    const renderDropdownOptions = (options: LookupValue[], placeholder = 'Select') => {
        if (isLookupsLoading) return <option value="">Loading...</option>;
        return (<><option value="">{placeholder}</option>{options.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}</>);
    };
console.log("isEditing----", isEditing);
console.log("showAddDependent----", showAddDependent);
    return (
        <div className="space-y-8 p-2 sm:p-4 md:p-6">
            <Notification status={operationStatus} onClose={() => setOperationStatus({ ...operationStatus, show: false })} />
            
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-8">
                <div className="lg:col-span-1">
                    <ProfileCard
                        profileData={profileData}
                        getLabel={getLabel}
                        onEdit={() => { if(profileData) { setEditedProfile(profileData); setIsEditing(true); } }}
                        onPictureChange={handleProfilePictureChange}
                    />
                </div>
                <div className="lg:col-span-3">
                    <DependentsSection
                        dependents={dependentsData}
                        apiLoading={apiLoading && dependentsData.length === 0}
                        onAdd={handleAddDependent}
                        onEdit={handleEditDependent}
                        onDelete={handleDeleteDependent}
                        getLabel={getLabel}
                    />
                </div>
            </div>
            {Boolean(isEditing) && (  
              <ProfileEditModal
                  isOpen={isEditing}
                  onClose={() => setIsEditing(false)}
                  profile={editedProfile}
                  setProfile={setEditedProfile}
                  onSave={handleProfileSave}
                  isSaving={isSavingProfile}
                  countries={countries}
                  states={statesForProfile}
                  cities={citiesForProfile}
                  currencies={currencies}
                  getOptions={getOptions}
                  renderDropdownOptions={renderDropdownOptions}
              />
            )}
            {Boolean(showAddDependent) && (  
              <DependentFormModal
                  isOpen={showAddDependent}
                  onClose={handleCancelDependent}
                  form={dependentForm}
                  setForm={setDependentForm}
                  onSubmit={handleDependentSubmit}
                  isSubmitting={isSubmittingDependent}
                  editingId={editingDependentId}
                  countries={countries}
                  states={statesForDependent}
                  cities={citiesForDependent}
                  currencies={currencies}
                  getOptions={getOptions}
                  renderDropdownOptions={renderDropdownOptions}
              />
            )}
        </div>
    );
};

export default Profile;